import React from "react";
import styled from "styled-components";

import AiCursor from "components/atoms/AiCursor";
import Icon from "components/atoms/Icon";
import Tooltip from "components/atoms/Tooltip";
import { buildInlineDiffSegments } from "components/organisms/manuscript/editor/ai/diff";
import { AI_EDIT_PRESETS } from "components/organisms/manuscript/editor/ai/presets";

const Backdrop = styled.div`
    position: fixed;
    inset: 0;
    z-index: 90;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(15, 23, 42, 0.48);
`;

const Shell = styled.div`
    display: flex;
    flex-direction: column;
    width: min(980px, calc(100vw - 32px));
    height: min(760px, calc(100vh - 32px));
    max-height: calc(100vh - 32px);
    border: 1px solid #d4dee8;
    border-radius: 16px;
    background: #ffffff;
    box-shadow: 0 20px 36px rgba(15, 23, 42, 0.24);
    overflow: hidden;
`;

const Header = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 14px 18px;
    border-bottom: 1px solid #e2e8f0;
`;

const HeaderTitle = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
`;

const Title = styled.div`
    font-size: 16px;
    font-weight: 800;
    color: #17314a;
`;

const Subtitle = styled.div`
    font-size: 12px;
    color: #64748b;
`;

const CloseButton = styled.button`
    height: 32px;
    padding: 0 12px;
    border: 1px solid #d6e0ea;
    border-radius: 8px;
    background: #ffffff;
    color: #32485e;
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;

    &:hover {
        background: #eef3f7;
    }
`;

const Body = styled.div`
    flex: 1;
    display: grid;
    grid-template-columns: ${(props) => (props.$previewMode ? "minmax(0, 1fr)" : "minmax(0, 1fr) minmax(0, 1fr)")};
    gap: 18px;
    padding: 16px 18px 18px;
    min-height: 0;
    overflow: hidden;
`;

const Column = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    min-height: 0;
    overflow: hidden;

    & + & {
        padding-left: 18px;
        border-left: 1px solid #e2e8f0;
    }
`;

const Section = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    min-height: 0;
    ${(props) => (props.$fill ? "flex: 1;" : "")}
`;

const SectionTitle = styled.div`
    font-size: 12px;
    font-weight: 800;
    color: #203249;
`;

const InfoCard = styled.div`
    padding: 12px;
    border: 1px solid #dfe7f0;
    border-radius: 12px;
    background: #f8fafc;
    font-size: 13px;
    line-height: 1.6;
    color: #203249;
    white-space: pre-wrap;
    word-break: break-word;
    overflow: auto;
`;

const SelectedTextCard = styled(InfoCard)`
    flex: 1;
    min-height: 0;
`;

const PromptBox = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 10px;
    border: 1px solid #d6e0eb;
    border-radius: 12px;
    background: #ffffff;
`;

const PromptInputRow = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 8px;
`;

const PromptPresetRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
`;

const PresetButton = styled.button`
    height: 30px;
    padding: 0 10px;
    border: 1px solid #d6e0ea;
    border-radius: 999px;
    background: #ffffff;
    color: #32485e;
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;

    &:hover {
        background: #eef3f7;
    }
`;

const PromptInput = styled.textarea`
    flex: 1;
    min-height: 70px;
    max-height: 150px;
    padding: 0;
    border: 0;
    background: #ffffff;
    color: #203249;
    font-size: 13px;
    line-height: 1.55;
    resize: vertical;

    &:focus {
        outline: none;
    }
`;

const RequestIconButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    flex: 0 0 34px;
    border: 1px solid #0f8f6f;
    border-radius: 9px;
    background: #0f8f6f;
    cursor: pointer;

    &:hover:not(:disabled) {
        background: #10765d;
    }

    &:disabled {
        border-color: #d6e0ea;
        background: #eef3f7;
        cursor: not-allowed;
        opacity: 0.75;
    }
`;

const ResponseBox = styled.div`
    flex: 1;
    min-height: 240px;
    padding: 12px;
    border: 1px solid #d6e0eb;
    border-radius: 12px;
    background: #ffffff;
    color: #203249;
    font-size: 13px;
    line-height: 1.6;
    white-space: pre-wrap;
    word-break: break-word;
    overflow: auto;
`;

const ResponsePlaceholder = styled.span`
    color: #8ba0b5;
`;

const PreviewShell = styled.div`
    display: flex;
    flex-direction: column;
    gap: 14px;
    flex: 1;
    min-height: 0;
    overflow: hidden;
`;

const PreviewGuide = styled.div`
    font-size: 15px;
    font-weight: 800;
    color: #17314a;
`;

const PreviewMeta = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const PreviewMetaTitle = styled.div`
    font-size: 13px;
    font-weight: 800;
    color: #203249;
`;

const PreviewMetaDescription = styled.div`
    font-size: 12px;
    line-height: 1.5;
    color: #5f7288;
`;

const DiffBox = styled.div`
    flex: 1;
    min-height: 0;
    padding: 14px 16px;
    border: 1px solid #d6e0eb;
    border-radius: 12px;
    background: #ffffff;
    overflow: auto;
    font-size: 14px;
    line-height: 1.7;
    color: #203249;
    white-space: pre-wrap;
    word-break: break-word;
`;

const DiffLegend = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
    font-size: 12px;
    color: #536981;
`;

const DiffLegendItem = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 6px;
`;

const DiffLegendSwatch = styled.span`
    width: 12px;
    height: 12px;
    border-radius: 3px;
    background: ${(props) => {
        if (props.$type === "add") return "rgba(15, 143, 111, 0.16)";
        if (props.$type === "remove") return "rgba(220, 38, 38, 0.14)";
        return "#e2e8f0";
    }};
    border: 1px solid
        ${(props) => {
            if (props.$type === "add") return "rgba(15, 143, 111, 0.35)";
            if (props.$type === "remove") return "rgba(220, 38, 38, 0.3)";
            return "#cfd8e3";
        }};
`;

const DiffSegment = styled.span`
    background: ${(props) => {
        if (props.$type === "add") return "rgba(15, 143, 111, 0.16)";
        if (props.$type === "remove") return "rgba(220, 38, 38, 0.14)";
        return "transparent";
    }};
    color: ${(props) => {
        if (props.$type === "add") return "#0f8f6f";
        if (props.$type === "remove") return "#b42318";
        return "#203249";
    }};
    text-decoration: ${(props) => (props.$type === "remove" ? "line-through" : "none")};
    border-radius: 4px;
`;

const LocationLabel = styled.div`
    font-size: 12px;
    font-weight: 700;
    color: #536981;
`;

const Message = styled.div`
    padding: 10px 12px;
    border-radius: 10px;
    background: ${(props) => (props.$error ? "#fff1f2" : "#f8fafc")};
    color: ${(props) => (props.$error ? "#b42318" : "#51657d")};
    font-size: 12px;
    line-height: 1.5;
`;

const Footer = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 12px;
    padding: 14px 18px 18px;
    border-top: 1px solid #eef2f6;
`;

const ActionRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
`;

const ActionButton = styled.button`
    height: 34px;
    padding: 0 14px;
    border: 1px solid ${(props) => (props.$primary ? "#0f8f6f" : "#d6e0ea")};
    border-radius: 9px;
    background: ${(props) => (props.$primary ? "#0f8f6f" : "#ffffff")};
    color: ${(props) => (props.$primary ? "#ffffff" : "#29415d")};
    font-size: 12px;
    font-weight: 800;
    cursor: pointer;

    &:hover:not(:disabled) {
        background: ${(props) => (props.$primary ? "#10765d" : "#eef3f7")};
    }

    &:disabled {
        opacity: 0.45;
        cursor: not-allowed;
    }
`;

export default function AIEditModal({
    nodeName,
    selectedText,
    locationLabel,
    prompt,
    responseText,
    isStreaming,
    hasResult,
    previewMode = false,
    errorMessage,
    statusMessage,
    onPromptChange,
    onRequest,
    onStop,
    onApply,
    onPreviewBack,
    onConfirmApply,
    onClose,
    onUsePreset,
}) {
    const diffSegments = previewMode ? buildInlineDiffSegments(selectedText, responseText) : [];
    const hasDiffContent = diffSegments.some((segment) => segment.value);

    return (
        <Backdrop
            onClick={() => {
                onClose?.();
            }}
        >
            <Shell
                data-testid="ai-edit-modal"
                onClick={(event) => {
                    event.stopPropagation();
                }}
            >
                <Header>
                    <HeaderTitle>
                        <Title>AI 편집</Title>
                        <Subtitle>{nodeName || "현재 파일"} 선택 영역을 AI로 편집합니다.</Subtitle>
                    </HeaderTitle>
                    <CloseButton type="button" onClick={onClose}>
                        닫기
                    </CloseButton>
                </Header>
                {previewMode ? (
                    <Body $previewMode>
                        <PreviewShell>
                            <PreviewGuide>아래와 같이 반영하시겠습니까?</PreviewGuide>
                            <PreviewMeta>
                                <PreviewMetaTitle>{nodeName || "현재 파일"}</PreviewMetaTitle>
                                <PreviewMetaDescription>
                                    {locationLabel
                                        ? `${locationLabel} 선택 영역을 아래 diff와 같이 교체합니다.`
                                        : "선택 영역을 아래 diff와 같이 교체합니다."}
                                </PreviewMetaDescription>
                            </PreviewMeta>
                            <DiffLegend>
                                <DiffLegendItem>
                                    <DiffLegendSwatch $type="remove" />
                                    삭제될 텍스트
                                </DiffLegendItem>
                                <DiffLegendItem>
                                    <DiffLegendSwatch $type="add" />
                                    추가될 텍스트
                                </DiffLegendItem>
                            </DiffLegend>
                            <DiffBox>
                                {hasDiffContent
                                    ? diffSegments.map((segment, index) => (
                                          <DiffSegment key={`${segment.type}-${index}`} $type={segment.type}>
                                              {segment.value}
                                          </DiffSegment>
                                      ))
                                    : "변경할 내용이 없습니다."}
                            </DiffBox>
                            {errorMessage ? <Message $error>{errorMessage}</Message> : null}
                            {statusMessage ? <Message>{statusMessage}</Message> : null}
                        </PreviewShell>
                    </Body>
                ) : (
                    <Body>
                        <Column>
                            <Section $fill>
                                <SectionTitle>편집할 텍스트</SectionTitle>
                                {locationLabel ? <LocationLabel>{locationLabel}</LocationLabel> : null}
                                <SelectedTextCard>{selectedText || "선택된 텍스트가 없습니다."}</SelectedTextCard>
                            </Section>
                            <PromptBox>
                                <PromptInputRow>
                                    <PromptInput
                                        value={prompt}
                                        onChange={(event) => onPromptChange?.(event.target.value)}
                                        placeholder="어떻게 편집할까요?"
                                    />
                                    <Tooltip message="요청" position="top">
                                        <RequestIconButton
                                            type="button"
                                            aria-label="요청"
                                            onClick={onRequest}
                                            disabled={isStreaming || !prompt.trim()}
                                        >
                                            <Icon
                                                name="arrowRight"
                                                size="12px"
                                                color={isStreaming || !prompt.trim() ? "#8ba0b5" : "#ffffff"}
                                            />
                                        </RequestIconButton>
                                    </Tooltip>
                                </PromptInputRow>
                                <PromptPresetRow>
                                    {AI_EDIT_PRESETS.map((preset) => (
                                        <PresetButton
                                            key={preset.id}
                                            type="button"
                                            onClick={() => onUsePreset?.(preset.id)}
                                        >
                                            {preset.label}
                                        </PresetButton>
                                    ))}
                                </PromptPresetRow>
                            </PromptBox>
                        </Column>
                        <Column>
                            <Section $fill>
                                <SectionTitle>AI 편집 결과</SectionTitle>
                                <ResponseBox>
                                    {responseText || (
                                        <ResponsePlaceholder>
                                            편집을 시작하면 결과가 여기에 표시됩니다.
                                        </ResponsePlaceholder>
                                    )}
                                    {isStreaming ? <AiCursor /> : null}
                                </ResponseBox>
                            </Section>
                            {errorMessage ? <Message $error>{errorMessage}</Message> : null}
                            {statusMessage ? <Message>{statusMessage}</Message> : null}
                        </Column>
                    </Body>
                )}
                <Footer>
                    <ActionRow>
                        {previewMode ? (
                            <>
                                <ActionButton type="button" onClick={onPreviewBack}>
                                    뒤로
                                </ActionButton>
                                <ActionButton type="button" $primary onClick={onConfirmApply}>
                                    확인
                                </ActionButton>
                                <ActionButton type="button" onClick={onClose}>
                                    취소
                                </ActionButton>
                            </>
                        ) : (
                            <>
                                <ActionButton type="button" onClick={onStop} disabled={!isStreaming}>
                                    중지
                                </ActionButton>
                                <ActionButton type="button" onClick={onApply} disabled={isStreaming || !hasResult}>
                                    반영
                                </ActionButton>
                                <ActionButton type="button" onClick={onClose}>
                                    취소
                                </ActionButton>
                            </>
                        )}
                    </ActionRow>
                </Footer>
            </Shell>
        </Backdrop>
    );
}
